import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../config';

// Google Analytics event tracking function
const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

const EmailSignupForm = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAlreadyRegistered(false);

    if (!email.trim()) {
      setError('Please enter your email address');
      trackEvent('email_signup_error', {
        error_type: 'empty_email',
        form_location: 'waitlist_signup'
      });
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      trackEvent('email_signup_error', {
        error_type: 'invalid_email',
        form_location: 'waitlist_signup'
      });
      return;
    }

    setIsLoading(true);

    try {
      // Send to your backend API endpoint
      const response = await fetch(API_ENDPOINTS.JOIN_WAITLIST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      let data;
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }

      if (!response.ok) {
        const serverMessage = data && data.message ? data.message : 'Failed to join waitlist';
        if (serverMessage.toLowerCase().includes('already registered')) {
          setAlreadyRegistered(true);
          trackEvent('email_signup_already_registered', {
            form_location: 'waitlist_signup'
          });
        } else {
          setError(serverMessage);
          trackEvent('email_signup_error', {
            error_type: 'server_error',
            error_message: serverMessage,
            form_location: 'waitlist_signup'
          });
        }
        return;
      }

      if (data && data.success) {
        setIsSubmitted(true);
        onSubmit(email);
        
        // Track successful signup
        trackEvent('email_signup_success', {
          form_location: 'waitlist_signup',
          email_domain: email.split('@')[1] // Track email domain for insights
        });
        
        // Store email in localStorage for demo purposes
        localStorage.setItem('claraai_waitlist_email', email);
      } else {
        setError((data && data.message) || 'Something went wrong. Please try again.');
        trackEvent('email_signup_error', {
          error_type: 'api_error',
          error_message: data?.message || 'Unknown error',
          form_location: 'waitlist_signup'
        });
      }
    } catch (err) {
      console.error('Error joining waitlist:', err);
      
      // For demo purposes, simulate success if API is not available
      if (process.env.NODE_ENV === 'development') {
        setIsSubmitted(true);
        onSubmit(email);
        trackEvent('email_signup_success', {
          form_location: 'waitlist_signup',
          email_domain: email.split('@')[1],
          environment: 'development'
        });
        localStorage.setItem('claraai_waitlist_email', email);
      } else {
        setError('Failed to join waitlist. Please try again later.');
        trackEvent('email_signup_error', {
          error_type: 'network_error',
          error_message: err.message,
          form_location: 'waitlist_signup'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto"
      >
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-800 mb-2">
            Welcome to ClaraAI!
          </h3>
          <p className="text-green-700 mb-4">
            You've been added to our waitlist. Check your email for confirmation and updates.
          </p>
          <div className="text-sm text-green-600">
            We'll notify you as soon as ClaraAI is ready for early access.
          </div>
        </div>
      </motion.div>
    );
  }

  if (alreadyRegistered) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto"
      >
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-blue-800 mb-2">
            You're already on the waitlist
          </h3>
          <p className="text-blue-700 mb-2">
            Great news — you're already signed up. We'll keep you posted!
          </p>
          <div className="text-sm text-blue-600">
            If you don't see our email, please check your spam folder.
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-md mx-auto"
      id="email-signup"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="w-5 h-5 text-clara-gray-400" />
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="input-field pl-15 pr-4 py-4 text-lg"
            disabled={isLoading}
          />
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center space-x-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-3"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </motion.div>
        )}

        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Joining Waitlist...</span>
            </div>
          ) : (
            'Join the Waitlist'
          )}
        </motion.button>

        <p className="text-xs text-clara-gray-500 text-center">
          By joining, you'll receive early access and exclusive updates about ClaraAI.
        </p>
      </form>
    </motion.div>
  );
};

export default EmailSignupForm; 